/**
 * Utility functions for API services
 * Reduces code repetition across API service files
 */

/**
 * Build a query string from an object of parameters
 * Filters out undefined/null values and handles type conversion
 *
 * @example
 * buildQueryString({ page: 1, search: 'test', status: undefined })
 * // Returns: "?page=1&search=test"
 *
 * @example
 * buildQueryString({ status: ['active', 'pending'] })
 * // Returns: "?status=active,pending"
 */
export function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) return '';

  const queryParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;

    if (Array.isArray(value)) {
      // Handle arrays as comma-separated values (e.g., status=active,pending)
      if (value.length > 0) {
        queryParams.set(key, value.join(','));
      }
    } else {
      queryParams.set(key, String(value));
    }
  }

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Common list parameters interface
 */
export interface BaseListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  search?: string;
  [key: string]: unknown;
}
